import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Alert, Box, Button, Card, CardContent, Chip, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  Tabs, TextField, Tooltip, Typography,
} from "@mui/material"
import VerifiedIcon from "@mui/icons-material/VerifiedOutlined"
import UndoIcon from "@mui/icons-material/UndoOutlined"
import ReviewIcon from "@mui/icons-material/FactCheckOutlined"
import ChevronIcon from "@mui/icons-material/ChevronRightOutlined"

import { timeApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import { decimalHours, humanDuration } from "../time/duration"
import { addDays, isoDay, weekLabel, weekRange } from "./time/weeks"
import { formatDate } from "../utils/format"
import UserAvatar from "../components/UserAvatar"
import PersonDialog from "./users/PersonDialog"
import { rise } from "../motion"

/**
 * The manager's review.
 *
 * Starts from people rather than entries: "these four have unsigned time" is a
 * morning's work, and a list of two hundred rows is not. Opening a person shows
 * what they actually did, which is the part worth reading before vouching for it.
 */

/** "RECON-2: Reconcile January · Northwind Wireless", as much as there is. */
function contextLine(context) {
  const work = context.task_code
    ? `${context.task_code}: ${context.task_title}`
    : context.project_code
      ? `${context.project_code} · ${context.project_name}`
      : context.project_name || "No project"
  return context.client_label ? `${work} — ${context.client_label}` : work
}

/**
 * The row's one line about what the time was for.
 *
 * Named outright when there is one thing, counted when there are several: five
 * client names truncated at the card's edge tells you less than "3 clients ·
 * 5 projects" and takes the same space. The full list is behind the chevron.
 */
function describeContexts(contexts, entryCount) {
  const entries = `${entryCount} ${entryCount === 1 ? "entry" : "entries"}`
  if (!contexts || contexts.length === 0) return entries
  if (contexts.length === 1) return contextLine(contexts[0])

  const clients = new Set(contexts.map((c) => c.client_label).filter(Boolean))
  const projects = new Set(contexts.map((c) => c.project_id).filter((id) => id != null))
  const parts = [entries]
  if (clients.size > 1) parts.push(`${clients.size} clients`)
  else if (clients.size === 1) parts.push([...clients][0])
  if (projects.size > 0) parts.push(`${projects.size} ${projects.size === 1 ? "project" : "projects"}`)
  return parts.join(" · ")
}
export default function TimeReviewPage() {
  const { can } = usePermissions()
  const navigate = useNavigate()
  const maySign = can("time_tracking", "sign_off")

  /*
   * A reviewer opening a person goes to their page when they can see people;
   * otherwise the small "who is this" dialog, which needs nothing more than
   * being able to see the person in this list.
   */
  const [personDialog, setPersonDialog] = useState(null)
  const openPerson = (id, name) => {
    if (!id) return
    if (can("users", "view")) navigate(`/users/${id}`)
    else setPersonDialog({ id, name })
  }

  const [tab, setTab] = useState("pending")
  const [anchor, setAnchor] = useState(() => new Date())
  const [people, setPeople] = useState([])
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [reviewing, setReviewing] = useState(null)
  const [expanded, setExpanded] = useState({})

  const range = useMemo(() => weekRange(anchor), [anchor])
  const from = isoDay(range.from)
  const to = isoDay(range.to)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [queue, signOffs] = await Promise.all([
        maySign ? timeApi.pendingSignOff({ from, to }) : Promise.resolve({ people: [] }),
        timeApi.signOffs({ limit: 50 }),
      ])
      setPeople(queue.people || [])
      setHistory(signOffs.sign_offs || [])
      setError(null)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [from, to, maySign])

  useEffect(() => {
    load()
  }, [load])

  const revoke = async (signOff) => {
    const reason = window.prompt(`Why is the sign-off for ${signOff.user_name} being taken back?`)
    if (reason === null) return
    try {
      const result = await timeApi.revokeSignOff(signOff.id, reason)
      setNotice(result.message)
      load()
    } catch (err) {
      setError(err.userMessage)
    }
  }

  const pendingSeconds = people.reduce((sum, p) => sum + p.unsigned_seconds, 0)

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Time review"
        subtitle="Check the team's hours and sign them off. Signing is not a gate on billing — it records that somebody looked."
      />

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert> : null}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="pending" label={`Waiting${people.length ? ` (${people.length})` : ""}`} />
        <Tab value="history" label="Signed off" />
      </Tabs>

      {tab === "pending" ? (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Button size="small" onClick={() => setAnchor(addDays(anchor, -7))}>Previous</Button>
                <Typography variant="h3" component="p" sx={{ minWidth: 150 }}>{weekLabel(anchor)}</Typography>
                <Button size="small" onClick={() => setAnchor(addDays(anchor, 7))}>Next</Button>
                <Button size="small" onClick={() => setAnchor(new Date())}>This week</Button>
                <Box sx={{ flex: 1 }} />
                <Chip
                  color={pendingSeconds > 0 ? "warning" : "success"}
                  variant="outlined"
                  label={pendingSeconds > 0 ? `${decimalHours(pendingSeconds)} h unsigned` : "Nothing waiting"}
                />
              </Stack>
            </CardContent>
          </Card>

          {loading ? (
            <Card><CardContent><LoadingRows rows={4} /></CardContent></Card>
          ) : people.length === 0 ? (
            <EmptyState
              icon={ReviewIcon}
              title="Nothing to review"
              description={maySign ? "Every hour logged this week has been signed off." : "You do not have permission to sign time off."}
            />
          ) : (
            <Stack spacing={1.5}>
              {people.map((person) => (
                <Card key={person.user_id}>
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      {/* The person opens their page: who they are, what else they
                          have been on, and the rest of their hours — the context
                          a reviewer wants before vouching for this week. */}
                      <Box
                        component="button"
                        type="button"
                        onClick={() => openPerson(person.user_id, person.name || person.username)}
                        aria-label={`Open ${person.name || person.username}`}
                        sx={{ appearance: "none", border: 0, p: 0, background: "none", cursor: "pointer", borderRadius: "50%", display: "inline-flex" }}
                      >
                        <UserAvatar
                          name={person.name || person.username}
                          avatarKey={person.avatar_key}
                          size={36}
                          tooltip={false}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <PersonName id={person.user_id} name={person.name || person.username} onOpen={openPerson} />
                        {/*
                          * What the time was against, in the row.
                          *
                          * Signing off is a statement that the work happened as
                          * described, so the description has to be on screen
                          * before the decision — a name, a count and a number
                          * is not something anybody can vouch for. One context
                          * is named outright; several are counted, and the full
                          * list is one click below.
                          */}
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {describeContexts(person.contexts, person.entry_count)} · {person.username}
                        </Typography>
                      </Box>
                      <Typography variant="h3" component="p" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {decimalHours(person.unsigned_seconds)} h
                      </Typography>
                      {(person.contexts || []).length > 1 ? (
                        <Tooltip title={expanded[person.user_id] ? "Hide the breakdown" : "What was this time on?"}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpanded((current) => ({ ...current, [person.user_id]: !current[person.user_id] }))
                            }
                            aria-label={`${expanded[person.user_id] ? "Hide" : "Show"} what ${person.name || person.username} worked on`}
                            aria-expanded={Boolean(expanded[person.user_id])}
                            sx={{
                              transform: expanded[person.user_id] ? "rotate(90deg)" : "none",
                              transition: "transform 160ms ease",
                            }}
                          >
                            <ChevronIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <Button variant="outlined" size="small" onClick={() => setReviewing(person)}>
                        Review
                      </Button>
                    </Stack>

                    <Collapse in={Boolean(expanded[person.user_id])} timeout={200} unmountOnExit>
                      <Stack spacing={0.75} sx={{ mt: 1.5, pl: 6 }}>
                        {(person.contexts || []).map((context, index) => (
                          <Stack
                            key={`${context.client_id}-${context.project_id}-${context.task_id}-${index}`}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }} noWrap>
                              {contextLine(context)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontVariantNumeric: "tabular-nums" }}
                            >
                              {decimalHours(context.seconds)} h
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Collapse>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      ) : (
        <Card>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 2 }}><LoadingRows rows={4} /></Box>
            ) : history.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <EmptyState icon={VerifiedIcon} title="Nothing signed off yet" description="Signed periods appear here." />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Person</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell>Signed by</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((s) => (
                    <TableRow key={s.id} sx={{ opacity: s.revoked_at ? 0.55 : 1 }}>
                      <TableCell>
                        {s.user_id ? <PersonName id={s.user_id} name={s.user_name} onOpen={openPerson} /> : s.user_name}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {String(s.period_start).slice(0, 10)} → {String(s.period_end).slice(0, 10)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {decimalHours(s.total_seconds)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {s.signed_by_name} · {formatDate(s.signed_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {s.revoked_at ? (
                          <Tooltip title={`Revoked by ${s.revoked_by_name}${s.revoke_reason ? `: ${s.revoke_reason}` : ""}`}>
                            <Chip size="small" label="Revoked" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                          </Tooltip>
                        ) : maySign ? (
                          <Button size="small" startIcon={<UndoIcon />} onClick={() => revoke(s)}>
                            Revoke
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <ReviewDialog
        person={reviewing}
        from={from}
        to={to}
        onClose={() => setReviewing(null)}
        onSigned={(message) => {
          setReviewing(null)
          setNotice(message)
          load()
        }}
      />
      <PersonDialog userId={personDialog?.id || null} fallbackName={personDialog?.name} onClose={() => setPersonDialog(null)} />
    </Box>
  )
}

/** What one person actually did, before vouching for it. */
function ReviewDialog({ person, from, to, onClose, onSigned }) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(false)
  const [note, setNote] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!person) return
    setLoading(true)
    setNote("")
    setError(null)
    timeApi
      .entries({ user_id: person.user_id, from, to, signed: "false", limit: 500 })
      .then((data) => setEntries(data.entries || []))
      .catch((err) => setError(err.userMessage))
      .finally(() => setLoading(false))
  }, [person, from, to])

  const sign = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await timeApi.signOff({
        user_id: person.user_id,
        period_start: from,
        period_end: to,
        note: note.trim() || null,
      })
      onSigned(`Signed off ${result.entries_signed} entries for ${person.name || person.username}.`)
    } catch (err) {
      setError(err.userMessage)
      setBusy(false)
    }
  }

  const total = entries.reduce((sum, e) => sum + (Number(e.duration_seconds) || 0), 0)

  return (
    <Dialog open={Boolean(person)} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {person?.name || person?.username}
        <Typography variant="body2" color="text.secondary">
          {from} → {to} · {decimalHours(total)} h across {entries.length} entries
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {loading ? (
          <LoadingRows rows={5} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Work</TableCell>
                <TableCell align="right">Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, index) => (
                <TableRow key={entry.id} hover sx={rise(index)}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Typography variant="caption">{formatDate(entry.started_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {entry.task_code ? `${entry.task_code}: ` : ""}
                      {entry.description || entry.task_title || "Untitled"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[entry.client_label, entry.project_name].filter(Boolean).join(" · ")}
                      {entry.is_billable ? "" : " · not billable"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {humanDuration(entry.duration_seconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Divider sx={{ my: 2 }} />
        <TextField
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          size="small"
          helperText="Kept with the sign-off, and visible to whoever bills these hours."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          startIcon={<VerifiedIcon />}
          onClick={sign}
          disabled={busy || loading || entries.length === 0}
        >
          {busy ? "Signing…" : `Sign off ${decimalHours(total)} h`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** A person's name that opens them, styled as a name rather than as a button. */
function PersonName({ id, name, onOpen }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(id, name)}
      sx={{
        appearance: "none",
        border: 0,
        p: 0,
        background: "none",
        font: "inherit",
        fontSize: 14,
        fontWeight: 600,
        color: "text.primary",
        cursor: "pointer",
        textAlign: "left",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
        "&:hover": { color: "primary.dark", textDecoration: "underline" },
      }}
    >
      {name}
    </Box>
  )
}
